import { useMemo } from 'react';
import {
  StyledAccordionSection,
  StyledContainer,
  StyledHeadline,
  StyledAccordionWrapper,
} from './Accordion.styles';
import AccordionItem from './components/AccordionItem/AccordionItem';
import StructuredData from '@/components/StructuredData/StructuredData';
import { graphql } from 'gatsby';
import { applyPaddingCSS } from '@/utils/generatePadding';
import { type BlockHTMLId } from '@/utils/renderBlock';

const Accordion = ({
  accordionElements: elements,
  system,
  id,
}: Queries.ACCORDIONFragment & BlockHTMLId) => {
  const { headline, items, type_of_schema_markup, bg_color } = elements;
  const accordionItems = items.value;
  const isFAQ = type_of_schema_markup?.value === 'faq_markup';

  const structuredData = useMemo(() => {
    if (!isFAQ) {
      return null;
    }
    const schemaFAQList = accordionItems.map((item) => {
      const htmlText = item.elements.content.value;
      const validText = htmlText.replace(/<[^>]*>?/gm, '');
      return {
        '@type': 'Question',
        name: item.elements.headline.value,
        acceptedAnswer: {
          '@type': 'Answer',
          text: validText,
        },
      };
    });

    return {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: schemaFAQList,
    };
  }, [accordionItems, isFAQ]);

  return (
    <StyledAccordionWrapper
      data-codename={system.codename}
      backgroundColor={bg_color?.value || undefined}
      id={id}
    >
      {structuredData && <StructuredData schema={structuredData} />}
      <StyledAccordionSection css={applyPaddingCSS(elements)}>
        <StyledContainer>
          <StyledHeadline title={headline.value} heading="h2">
            {headline.value}
          </StyledHeadline>
          {accordionItems.map((item) => (
            <AccordionItem key={item.system.id} {...item} />
          ))}
        </StyledContainer>
      </StyledAccordionSection>
    </StyledAccordionWrapper>
  );
};

export default Accordion;

export const fragment = graphql`
  fragment ACCORDION on kontent_item_accordion {
    __typename
    system {
      codename
      id
      type
    }
    accordionElements: elements {
      ...COMPONENT_A_B_TEST
      ...PADDING
      headline {
        value
      }
      items {
        value {
          ...ACCORDION_ITEM
        }
      }
      type_of_schema_markup {
        value
      }
      bg_color {
        value
      }
    }
  }
`;
